import { Button, Checkbox, Input } from 'antd';

import { useViewNameMaxLength } from 'hooks/ppa/usePpaViews';

import { ViewNameClearIcon } from '../SavedViewStateSelect.icons';
import { getViewNameValidationError } from '../savedViewStateSelect.helpers';
import { VIEW_NAME_MAX_LENGTH } from '../savedViewStateSelect.constants';
import { VIEW_NAME_INPUT_CLASS, VIEW_NAME_INPUT_ERROR_CLASS } from '../savedViewStateSelect.styles';
import type { SaveCurrentViewFormProps } from '../SavedViewStateSelect.types';
import SavedViewsEmptyState from './SavedViewsEmptyState';

const SaveCurrentViewForm = ({
    showEmptyHeader,
    formState,
    existingViewNames,
    isSaving,
    onFormChange,
    onCancel,
    onSave,
}: SaveCurrentViewFormProps) => {
    const viewNameMaxLength = useViewNameMaxLength() ?? VIEW_NAME_MAX_LENGTH;
    const viewNameError = getViewNameValidationError(formState.viewName, existingViewNames);
    const trimmedViewName = formState.viewName.trim();
    const canSave = Boolean(trimmedViewName) && !viewNameError && !isSaving;

    return (
        <div className="flex flex-col gap-5">
            {showEmptyHeader ? <SavedViewsEmptyState className="px-3" /> : null}
            <div className="flex flex-col gap-2 px-3">
                <div className="flex flex-col gap-1">
                    <Input
                        size="large"
                        value={formState.viewName}
                        onChange={(event) => onFormChange((prev) => ({ ...prev, viewName: event.target.value }))}
                        placeholder="Enter View name"
                        maxLength={viewNameMaxLength}
                        showCount
                        allowClear={
                            formState.viewName
                                ? { clearIcon: <ViewNameClearIcon hasError={Boolean(viewNameError)} /> }
                                : false
                        }
                        status={viewNameError ? 'error' : undefined}
                        className={viewNameError ? VIEW_NAME_INPUT_ERROR_CLASS : VIEW_NAME_INPUT_CLASS}
                    />
                    {viewNameError ? (
                        <span className="text-[12px] leading-[16px] text-[#ff4d4f]">{viewNameError}</span>
                    ) : null}
                </div>
                <Checkbox
                    checked={formState.includeAppliedFilters}
                    onChange={(event) =>
                        onFormChange((prev) => ({ ...prev, includeAppliedFilters: event.target.checked }))
                    }
                    className="!m-0 !text-[14px] !text-fontColor"
                >
                    Include applied filters
                </Checkbox>
                <Checkbox
                    checked={formState.setAsDefault}
                    onChange={(event) => onFormChange((prev) => ({ ...prev, setAsDefault: event.target.checked }))}
                    className="!m-0 !text-[14px] !text-fontColor"
                >
                    Set as default
                </Checkbox>
                <div className="mt-1 flex gap-2">
                    <Button
                        className="toolbar-btn !h-[32px] !flex-1 !rounded-lg !text-[14px] !font-medium !text-[#52525c]"
                        onClick={onCancel}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        disabled={!canSave}
                        loading={isSaving}
                        className="!h-[32px] !w-[108px] !rounded-lg !border-[#44338a] !bg-[#44338a] !font-medium !text-white disabled:!border-[#d9d9d9] disabled:!bg-[#f5f5f5] disabled:!text-black/25"
                        onClick={onSave}
                    >
                        Save
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default SaveCurrentViewForm;
